import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Controller} from 'react-hook-form'
import {
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    InputGroup,
    Tooltip
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {InfoIcon} from '@salesforce/retail-react-app/app/components/icons'
import {formInputStyle, formLabelStyle, giftCardNumberTooltip} from './giftCartPayment.style'
import {useIntl} from 'react-intl'
import {pxToRem} from '../../../../utills/pxToRem'
import colors from '../../../../theme/foundations/colors'

const GiftCardField = ({
    name,
    label,
    formLabel,
    type = 'text',
    options = [],
    rules = {},
    error,
    placeholder,
    inputProps,
    control,
    autoComplete,
    defaultValue,
    helpText,
    children,
    inputRef,
    width = '100%'
}) => {
    const [hidePassword, setHidePassword] = useState(true)
    const {formatMessage} = useIntl()

    const inputType =
        type === 'password' && hidePassword ? 'password' : type === 'password' ? 'text' : type

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue}
            render={({field: {onChange, value, ref}}) => {
                const _inputProps =
                    typeof inputProps === 'function' ? inputProps({value, onChange}) : inputProps

                return (
                    <FormControl id={name} isInvalid={error} width={children ? 'auto' : width}>
                        <FormLabel
                            sx={formLabelStyle}
                            marginTop={
                                label === 'Pin'
                                    ? [pxToRem(20), pxToRem(20), pxToRem(10), pxToRem(10)]
                                    : [pxToRem(10)]
                            }
                            marginBottom={pxToRem(10)}
                        >
                            {label}
                            <Tooltip
                                placement="top"
                                label={
                                    'If you wish to use a gift card to pay for some or all of your order, please enter your gift card number and click "Apply".'
                                }
                                shouldWrapChildren={true}
                                sx={giftCardNumberTooltip}
                            >
                                {label !== 'Pin' && (
                                    <InfoIcon
                                        color="gray.700"
                                        sx={{
                                            height: pxToRem(15),
                                            width: pxToRem(15),
                                            marginLeft: pxToRem(5)
                                        }}
                                        aria-label={formatMessage({
                                            id: 'credit_card_fields.tool_tip.security_code_aria_label',
                                            defaultMessage:
                                                'If you wish to use a gift card to pay for some or all of your order, please enter your gift card number and click "Apply"'
                                        })}
                                    />
                                )}
                            </Tooltip>
                        </FormLabel>
                        <InputGroup>
                            <Input
                                ref={(node) => {
                                    ref(node)
                                    if (inputRef) inputRef.current = node
                                }}
                                onChange={(e) => {
                                    const val = e.target.value
                                    const filtered = val.replace(/[^0-9]/g, '')
                                    onChange(filtered)
                                }}
                                value={value}
                                type={inputType}
                                placeholder={placeholder}
                                autoComplete={autoComplete}
                                maxLength={label == 'Pin' ? 4 : 19}
                                {..._inputProps}
                                isInvalid={!!error}
                                errorBorderColor="black"
                                boxShadow="none !important"
                                width={width}
                                sx={{
                                    ...formInputStyle,
                                    ...(error && {
                                        _focus: {
                                            backgroundColor: colors.whiteRed[20],
                                            borderColor: 'cav_error_red',
                                            boxShadow: 'none',
                                            outline: 'none'
                                        }
                                    })
                                }}
                            />
                            {children}
                        </InputGroup>

                        {/* {error && type !== 'hidden' && (
                            <FormErrorMessage sx={formErrorMessageStyle}>
                                <AlertIcon aria-hidden="true" mr={2} />
                                {error.message}
                            </FormErrorMessage>
                        )} */}

                        {helpText}
                    </FormControl>
                )
            }}
        />
    )
}

GiftCardField.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    autoComplete: PropTypes.string,
    formLabel: PropTypes.any,
    type: PropTypes.oneOf([
        'text',
        'number',
        'password',
        'email',
        'phone',
        'tel',
        'select',
        'checkbox',
        'hidden'
    ]),
    options: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string, value: PropTypes.any})),
    rules: PropTypes.object,
    error: PropTypes.shape({message: PropTypes.string}),
    placeholder: PropTypes.string,
    inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    control: PropTypes.object,
    defaultValue: PropTypes.any,
    helpText: PropTypes.any,
    children: PropTypes.any,
    inputRef: PropTypes.object,
    width: PropTypes.string
}

export default GiftCardField
